import React from 'react';

/**
 * Global error boundary — wraps the entire app router so that any thrown
 * runtime error (broken integration, missing module, undefined.something)
 * shows a contained recovery card instead of a blank white page.
 *
 * Crucially: we never let one broken category bring down the whole site.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info);
  }

  resetAndGoHome = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || 'Unknown error';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 text-center" data-testid="global-error-boundary">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 mx-auto flex items-center justify-center text-2xl mb-3">!</div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Something went wrong on this page</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Don't worry — the rest of Elite Travo is still working. Try again, or head back to the home page.
          </p>
          <details className="text-left text-xs text-gray-400 dark:text-gray-500 mb-4">
            <summary className="cursor-pointer mb-1">Technical detail</summary>
            <pre className="whitespace-pre-wrap break-words">{message}</pre>
          </details>
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.reload}
              className="rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
              data-testid="error-reload-btn"
            >
              Try again
            </button>
            <button
              onClick={this.resetAndGoHome}
              className="rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2"
              data-testid="error-home-btn"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
